import * as errors from '@attain-apps/errors'
import { TypeChecker } from '@attain-apps/type-guards'
import _isEqual from 'lodash-es/isEqual'

export namespace Request {
  export namespace For {
    /** Contains interfaces for various types of pagination */
    export namespace Pagination {
      /**
       * Used to request for a page of data at a time where the data can be
       * retrieved from a particular position (e.g. SQL or in-memory databases)
       *
       * @example
       * // Retrieve the fourth page of data where the page size is 100
       * const request: Request.For.Pagination.ByPosition = {
       *   quantity: 100,
       *   skip: 300,
       * }
       *
       * // How this might be used
       * const { quantity, skip = 0 } = request
       * return items.slice(
       *   skip,
       *   quantity !== undefined ? skip + quantity : undefined
       * )
       */
      export interface ByPosition {
        /**
         * Max number of items desired
         *
         * @default no limit
         */
        quantity?: number
        /**
         * The first item retrieved should be in this position (using a
         * zero-based index)
         *
         * @default 0
         */
        skip?: number
      }
      export namespace ByPosition {
        export const isByPosition = TypeChecker.Factory.object<ByPosition>({
          quantity: TypeChecker.Factory.optional(TypeChecker.isNumber),
          skip: TypeChecker.Factory.optional(TypeChecker.isNumber),
        })

        /**
         * Sets the default values for a
         * {@link Request.For.Pagination.ByPosition}
         */
        export type WithDefaults = Required.Semi<ByPosition, 'skip'>
        export namespace WithDefaults {
          export function from(request: ByPosition): WithDefaults {
            return { ...request, skip: request.skip ?? 0 }
          }
        }

        /**
         * Returns `true` if the pagination request may limit the number of
         * results. Returns false if the pagination request is guaranteed to
         * return the entire list of items
         */
        export function isPaginated(request: ByPosition): boolean {
          const { skip, quantity } = WithDefaults.from(request)
          return skip !== 0 || quantity !== undefined
        }

        /**
         * Determines if two pagination requests are effectively equivalent,
         * taking into account default values
         */
        export function isEqual(
          a: ByPosition | undefined,
          b: ByPosition | undefined,
        ): boolean {
          if (a === b) {
            return true
          }
          if (a === undefined || b === undefined) {
            return false
          }
          const aDef = WithDefaults.from(a)
          const bDef = WithDefaults.from(b)
          return aDef.quantity === bDef.quantity && aDef.skip === bDef.skip
        }
        /**
         * Determines if all the data retrieved with `b` is already retrieved
         * with `a`
         */
        export function isSupersededBy(a: ByPosition, b: ByPosition): boolean {
          const aDef = WithDefaults.from(a)
          const bDef = WithDefaults.from(b)
          const aStart = aDef.skip * (aDef.quantity ?? 0)
          const aEnd = aStart + (aDef.quantity ?? Infinity)
          const bStart = bDef.skip * (bDef.quantity ?? 0)
          const bEnd = bStart + (bDef.quantity ?? Infinity)
          return aStart <= bStart && aEnd >= bEnd
        }
        /**
         * Determines if some of the data retrieved with `b` is also retrieved
         * with `a`
         */
        export function overlaps(a: ByPosition, b: ByPosition): boolean {
          const aDef = WithDefaults.from(a)
          const bDef = WithDefaults.from(b)
          const aStart = aDef.skip ?? 0 * (aDef.quantity ?? 0)
          const aEnd = aStart + (aDef.quantity ?? Infinity)
          const bStart = bDef.skip ?? 0 * (bDef.quantity ?? 0)
          const bEnd = bStart + (bDef.quantity ?? Infinity)
          return bStart <= aEnd && aStart <= bEnd
        }

        /**
         * Determins if given offset/position is within the given pagination
         * request
         */
        export function isOnPage(
          offset: number,
          { skip, quantity }: ByPosition,
        ): boolean {
          return (
            (skip === undefined || offset >= skip) &&
            (quantity === undefined || offset < (skip ?? 0) + quantity)
          )
        }

        /**
         * Determines page number based on given parameters
         *
         * Starts at `1`
         *
         * __NOTE:__ uses {@link assertCanDeterminePageNumber}
         *
         * @example
         * getPageNumber({}) // 1
         * getPageNumber({ quantity: 100 }) // 1
         * getPageNumber({ quantity: 100, skip: 0 }) // 1
         * getPageNumber({ quantity: 100, skip: 100 }) // 2
         * getPageNumber({ quantity: 100, skip: 400 }) // 5
         * getPageNumber({ skip: 400 }) // Throws error
         * getPageNumber({ quantity: 0, skip: 400 }) // Throws error
         */
        export function getPageNumber(request: ByPosition): number {
          assertCanDeterminePageNumber(request)
          const { skip = 0, quantity = 0 } = request
          if (skip === 0 || quantity === 0) {
            return 1
          }
          return Math.floor(skip / quantity) + 1
        }
        /**
         * We cannot determine the page number when `skip` is a non-zero number
         * and `quantity` is not
         */
        export function canDeterminePageNumber({
          quantity = 0,
          skip = 0,
        }: ByPosition): boolean {
          return skip === 0 || quantity > 0
        }
        /**
         * Throws a {@link errors.ValidationError ValidationError} when
         * {@link canDeterminePageNumber} returns `false`
         */
        export function assertCanDeterminePageNumber(
          pagination: ByPosition,
        ): void {
          if (!canDeterminePageNumber(pagination)) {
            throw new errors.ValidationError({
              reason:
                "Cannot determine page number when 'skip' is a non-zero number and 'quantity' is not",
              type: 'pagination request for determining page number',
              value: pagination,
            })
          }
        }

        export function getSkip({
          pageNumber,
          quantity,
        }: getSkip.Params): number {
          return (pageNumber - 1) * quantity
        }
        export namespace getSkip {
          export interface Params {
            pageNumber: number
            quantity: number
          }
        }
      }

      // TODO: create interface for DynamoDB and other cursor-based pagination
      // systems

      // export interface ByCursor
    }

    type SortBase = Sort.DirectionOnly
    /**
     * Sorts by something in particular. This is often an attribute of or
     * computation on the individual item
     *
     * @example
     * // Sorts by the 'name' attribute in ascending order
     * const request: Request.For.Sort<keyof MyItem> = {
     *   by: 'name'
     * }
     *
     * // Sorts by the length of the 'name' attribute in descending order
     * const request: Request.For.Sort<(item: MyItem) => number> = {
     *   by: item => item.name.length,
     *   descending: true
     * }
     */
    export interface Sort<TBy = unknown> extends SortBase {
      /**
       * What the data should be sorted by
       */
      by: TBy
    }
    export namespace Sort {
      export function getTypeChecker<TBy>(
        isBy: TypeChecker<TBy>,
      ): TypeChecker<Sort<TBy>> {
        return TypeChecker.Factory.intersection(
          DirectionOnly.isDirectionOnly,
          TypeChecker.Factory.object<Difference<Sort<TBy>, SortBase>>({
            by: isBy,
          }),
        ).usingAlias(() => `Request.For.Sort<${isBy.typeDescription}>`)
      }
      export const isSort: TypeChecker<Sort> = TypeChecker.Factory.lazy(() =>
        getTypeChecker(TypeChecker.isUnknown),
      ).usingAlias('Request.For.Sort')

      /**
       * Use when there is only one thing to sort on, and the question is just
       * which direction to sort
       *
       * Otherwise, use {@link Sort}
       */
      export interface DirectionOnly {
        /**
         * If `true`, sort is in descending order. If `false`, sort is in
         * ascending order
         *
         * @default false
         */
        descending?: boolean
      }
      export namespace DirectionOnly {
        export const isDirectionOnly =
          TypeChecker.Factory.object<DirectionOnly>({
            descending: TypeChecker.Factory.optional(TypeChecker.isBoolean),
          })

        /**
         * Sets the default values for a
         * {@link Request.For.Sort.DirectionOnly}
         */
        // eslint-disable-next-line @typescript-eslint/no-shadow
        export type WithDefaults = Required<DirectionOnly>
        export namespace WithDefaults {
          export function from({
            descending = false,
          }: DirectionOnly): WithDefaults {
            return { descending }
          }
        }

        /**
         * Determines if two direction-only sorting requests are effectively
         * equivalent, taking into account default values
         */
        // eslint-disable-next-line @typescript-eslint/no-shadow
        export function isEqual(
          a: DirectionOnly | undefined,
          b: DirectionOnly | undefined,
        ): boolean {
          if (a === b) {
            return true
          }
          if (a === undefined || b === undefined) {
            return false
          }
          const aDef = WithDefaults.from(a)
          const bDef = WithDefaults.from(b)
          return aDef.descending === bDef.descending
        }
      }

      /**
       * Sets the default values for a {@link Request.For.Sort}
       */
      export type WithDefaults<TBy = unknown> = Required<Sort<TBy>>
      export namespace WithDefaults {
        export function from<TSort extends Sort>(
          request: TSort,
        ): TSort extends Sort<infer TBy> ? WithDefaults<TBy> : WithDefaults
        export function from<TSort extends Sort>(
          request: TSort | undefined,
        ):
          | (TSort extends Sort<infer TBy> ? WithDefaults<TBy> : WithDefaults)
          | undefined
        export function from(
          request: Sort | undefined,
        ): WithDefaults | undefined {
          return (
            request && {
              ...request,
              ...DirectionOnly.WithDefaults.from(request),
            }
          )
        }
      }

      /**
       * Determines if two sorting requests are effectively equivalent, taking
       * into account default values
       */
      export function isEqual<TBy>(
        a: Sort<TBy> | undefined,
        b: Sort<TBy> | undefined,
        { byIsEqual = _isEqual }: isEqual.Options<TBy> = {},
      ): boolean {
        if (a === b) {
          return true
        }
        if (a === undefined || b === undefined) {
          return false
        }
        const aDef = WithDefaults.from(a)
        const bDef = WithDefaults.from(b)
        return byIsEqual(aDef.by, bDef.by) && DirectionOnly.isEqual(a, b)
      }
      export namespace isEqual {
        export interface Options<TBy> {
          byIsEqual?: (a: TBy, b: TBy) => boolean
        }
      }
    }
  }
}
